import api from './api';

const buildGroupsQueryString = (filters = {}, pagination = null) => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params.append(key, value);
    }
  });

  if (pagination?.showAll) {
    params.append('showAll', 'true');
  } else if (pagination) {
    params.append('page', pagination.page);
    params.append('limit', pagination.pageSize);
  }

  return params.toString();
};

const groupService = {
  // Get count of groups by filters without loading data
  getGroupsCount: async (filters = {}) => {
    const params = buildGroupsQueryString(filters);
    const response = await api.get(`/groups/count?${params}`);
    return response.data;
  },

  // Get all groups with filters and pagination
  getGroups: async (filters = {}, pagination = { page: 1, pageSize: 100 }) => {
    const params = buildGroupsQueryString(filters, pagination);
    const response = await api.get(`/groups?${params.toString()}`);
    // Backend returns {groups: [...], pagination: {...}}
    return response.data;
  },

  exportGroups: async (filters = {}) => {
    const params = buildGroupsQueryString(filters);
    return api.get(`/groups/export?${params}`, {
      responseType: 'blob',
    });
  },

  // Get categories
  getCategories: async () => {
    const response = await api.get('/groups/categories');
    return response.data;
  },

  // Get distinct options for group filters
  getFilterOptions: async () => {
    const response = await api.get('/groups/filter-options');
    return response.data;
  },

  // Get group by ID
  getGroup: async (id) => {
    const response = await api.get(`/groups/${id}`);
    return response.data;
  },

  // Update group
  updateGroup: async (id, groupData) => {
    const response = await api.put(`/groups/${id}`, groupData);
    return response.data;
  },

  // Delete group
  deleteGroup: async (id) => {
    const response = await api.delete(`/groups/${id}`);
    return response.data;
  },

  // Add bulk groups
  addBulkGroups: async (data) => {
    const response = await api.post('/groups/add-bulk', data);
    return response.data;
  },

  searchPublicGroup: async (username) => {
    const response = await api.get('/groups/search-public', {
      params: { username },
    });
    return response.data;
  },

  subscribeAndAddPublicGroup: async (data) => {
    const response = await api.post('/groups/subscribe-public', data);
    return response.data;
  },
  
  // Get detailed group info
  getGroupInfo: async (id) => {
    const response = await api.post(`/groups/${id}/info`);
    return response.data;
  },

  // Analyze single group
  analyzeGroup: async (id, options = {}) => {
    const response = await api.post(`/groups/${id}/analyze`, options);
    return response.data;
  },

  // Batch analyze groups
  async batchAnalyze(data) {
    const response = await api.post('/groups/batch-analyze', data);
    return response.data;
  },

  // Batch get basic info for groups
  async batchGetBasicInfo(data) {
    const response = await api.post('/groups/batch-basic-info', data);
    return response.data;
  },

  // Get basic info process status
  async getBasicInfoProcessStatus(processId) {
    const response = await api.get(`/groups/basic-info-process/${processId}`);
    return response.data;
  },

  // Get all basic info processes
  async getBasicInfoProcesses() {
    const response = await api.get('/groups/basic-info-processes');
    return response.data;
  },

  // Cancel basic info process
  async cancelBasicInfoProcess(processId) {
    const response = await api.delete(`/groups/basic-info-process/${processId}`);
    return response.data;
  },

  // Update group category
  updateGroupCategory: async (id, category) => {
    const response = await api.put(`/groups/${id}/category`, { category });
    return response.data;
  },

  // Get group stats
  getGroupStats: async () => {
    const response = await api.get('/groups/stats');
    return response.data;
  },

  // Scan accounts for new groups (monitored)
  scanAccounts: async (options = {}) => {
    const response = await api.post('/scanner/scan-all', options);
    return response.data;
  },

  // Get account scanner process status
  async getAccountScannerProcessStatus(processId) {
    const response = await api.get(`/scanner/process/${processId}`);
    return response.data.process;
  },

  // Cancel account scanner process
  async cancelAccountScannerProcess(processId) {
    const response = await api.post(`/scanner/process/${processId}/cancel`);
    return response.data;
  },

  // Queue analysis for scanned groups
  queueAnalysis: async (options = {}) => {
    const response = await api.post('/scanner/queue-analysis', options);
    return response.data;
  },

  // Get scanner status
  getScannerStatus: async () => {
    const response = await api.get('/scanner/status');
    return response.data;
  },

  // Get active long-running processes
  getActiveProcesses: async () => {
    const response = await api.get('/processes/active');
    return response.data;
  },

  // Start AI analysis process
  async startAIAnalysis(chatIds, options = {}) {
    const response = await api.post('/groups/start-ai-analysis', {
      chatIds,
      options
    });
    return response.data;
  },

  // Get AI analysis process status
  async getAIAnalysisProcessStatus(processId) {
    const response = await api.get(`/groups/ai-analysis-process/${processId}`);
    return response.data;
  },

  // Cancel AI analysis process
  async cancelAIAnalysisProcess(processId) {
    const response = await api.post(`/groups/cancel-ai-analysis/${processId}`);
    return response.data;
  },

  // Batch join groups
  async batchJoinGroups(data) {
    const response = await api.post('/groups/batch-join', data);
    return response.data;
  },

  // Get join process status
  async getJoinProcessStatus(processId) {
    const response = await api.get(`/groups/join-process/${processId}`);
    return response.data;
  },

  // Get all join processes
  async getJoinProcesses() {
    const response = await api.get('/groups/join-processes');
    return response.data;
  },

  // Cancel join process
  async cancelJoinProcess(processId) {
    const response = await api.delete(`/groups/join-process/${processId}`);
    return response.data;
  },

  // Batch unsubscribe from groups
  async batchUnsubscribe(data) {
    const response = await api.post('/groups/batch-unsubscribe', data);
    return response.data;
  },

  // Get unsubscribe process status
  async getUnsubscribeProcessStatus(processId) {
    const response = await api.get(`/groups/unsubscribe-process/${processId}`);
    return response.data;
  },

  // Get all unsubscribe processes
  async getUnsubscribeProcesses() {
    const response = await api.get('/groups/unsubscribe-processes');
    return response.data;
  },

  // Cancel unsubscribe process
  async cancelUnsubscribeProcess(processId) {
    const response = await api.delete(`/groups/unsubscribe-process/${processId}`);
    return response.data;
  },

  // Unsubscribe from single group
  async unsubscribeFromGroup(chatId) {
    const response = await api.post(`/groups/${chatId}/unsubscribe`);
    return response.data;
  },

  // Start subscription check process
  async startSubscriptionCheck(data) {
    const response = await api.post('/groups/check-subscriptions', data);
    return response.data;
  },

  // Get subscription check process status
  async getSubscriptionCheckProcessStatus(processId) {
    const response = await api.get(`/groups/check-subscriptions/${processId}`);
    return response.data;
  },

  // Get subscription check statistics
  async getSubscriptionStats() {
    const response = await api.get('/groups/subscription-stats');
    return response.data;
  },

  // Cancel subscription check process
  async cancelSubscriptionCheckProcess(processId) {
    const response = await api.delete(`/groups/check-subscriptions/${processId}`);
    return response.data;
  },

  // Cleanup all duplicate Basic Info processes (keep only latest)
  async cleanupAllProcesses() {
    const response = await api.post('/groups/cleanup-all-processes');
    return response.data;
  }
};

export default groupService;
